import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, X, Check, ChevronDown, Building2, Loader2 } from 'lucide-react';

/**
 * Enterprise-grade searchable multi-select dropdown for projects
 * Supports server-side search for large datasets (4000+ projects)
 */
const ProjectMultiSelect = ({ 
  projects = [], 
  selectedIds = [], 
  onChange,
  onSearch, // Optional: callback for server-side search (query) => Promise<projects[]>
  placeholder = "Select projects shown",
  error = false,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProjectsCache, setSelectedProjectsCache] = useState([]);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Debounce search query for performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Perform server-side search when query changes
  useEffect(() => {
    const performSearch = async () => {
      if (!onSearch) return;
      
      if (!debouncedQuery.trim()) {
        setSearchResults([]);
        return;
      }
      
      setIsSearching(true);
      try {
        const results = await onSearch(debouncedQuery);
        setSearchResults(results || []);
      } catch (error) {
        console.error('Project search failed:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };
    
    performSearch();
  }, [debouncedQuery, onSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Cache selected projects for display (since they might come from search results)
  useEffect(() => {
    const allProjects = [...projects, ...searchResults];
    const selectedFromAll = allProjects.filter(p => selectedIds.includes(p.project_id));
    
    // Merge with existing cache (keep projects that are selected but not in current lists)
    setSelectedProjectsCache(prev => {
      const newCache = [...selectedFromAll];
      prev.forEach(cached => {
        if (selectedIds.includes(cached.project_id) && 
            !newCache.find(p => p.project_id === cached.project_id)) {
          newCache.push(cached);
        }
      });
      return newCache;
    });
  }, [projects, searchResults, selectedIds]);

  // Determine which projects to display
  const displayProjects = useMemo(() => {
    // If server-side search is enabled and there's a query, use search results
    if (onSearch && debouncedQuery.trim()) {
      return searchResults;
    }
    
    // Otherwise, filter client-side from provided projects
    if (!debouncedQuery.trim()) return projects;
    
    const query = debouncedQuery.toLowerCase();
    return projects.filter(project => {
      const name = (project.name || project.project_name || '').toLowerCase();
      const location = (project.location || '').toLowerCase();
      const city = (project.city || '').toLowerCase();
      const builder = (project.builder_name || '').toLowerCase();
      return name.includes(query) || location.includes(query) || city.includes(query) || builder.includes(query);
    });
  }, [projects, searchResults, debouncedQuery, onSearch]);

  // Get selected projects for chip display
  const selectedProjects = useMemo(() => {
    return selectedProjectsCache.filter(p => selectedIds.includes(p.project_id));
  }, [selectedProjectsCache, selectedIds]);

  const toggleProject = useCallback((project) => {
    const projectId = project.project_id;
    const newSelection = selectedIds.includes(projectId)
      ? selectedIds.filter(id => id !== projectId)
      : [...selectedIds, projectId];
    
    // Add to cache if selecting
    if (!selectedIds.includes(projectId)) {
      setSelectedProjectsCache(prev => {
        if (!prev.find(p => p.project_id === projectId)) {
          return [...prev, project];
        }
        return prev;
      });
    }
    
    onChange(newSelection);
  }, [selectedIds, onChange]);

  const removeProject = (projectId, e) => {
    e.stopPropagation();
    onChange(selectedIds.filter(id => id !== projectId));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      {/* Trigger Button / Selected Chips Display */}
      <div
        data-testid="project-multiselect-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`min-h-[42px] w-full px-3 py-2 rounded-lg border transition-all cursor-pointer
          ${disabled ? 'bg-slate-100 cursor-not-allowed' : 'bg-white hover:border-slate-400'}
          ${error ? 'border-red-400 focus-within:ring-2 focus-within:ring-red-100' : 'border-slate-200'}
          ${isOpen ? 'border-slate-400 ring-2 ring-slate-100' : ''}
        `}
      >
        <div className="flex items-center gap-2 flex-wrap">
          {selectedProjects.length === 0 ? (
            <span className="text-slate-400 text-sm">{placeholder}</span>
          ) : (
            <>
              {/* Show chips for selected projects */}
              {selectedProjects.slice(0, 3).map(project => (
                <span
                  key={project.project_id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-md font-medium"
                >
                  <span className="truncate max-w-[120px]">
                    {project.name || project.project_name}
                  </span>
                  <button
                    onClick={(e) => removeProject(project.project_id, e)}
                    className="hover:bg-slate-200 rounded p-0.5 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              {selectedProjects.length > 3 && (
                <span className="text-xs text-slate-500 font-medium">
                  +{selectedProjects.length - 3} more
                </span>
              )}
            </>
          )}
          <ChevronDown 
            size={16} 
            className={`ml-auto text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div 
          className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
          style={{ maxHeight: '320px' }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-slate-100 bg-slate-50">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchInputRef}
                type="text"
                data-testid="project-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects by name, location..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-200"
              />
              {searchQuery && !isSearching && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
              {isSearching && (
                <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
              )}
            </div>
            {/* Search hint */}
            {onSearch && !searchQuery && (
              <p className="text-xs text-slate-400 mt-1 px-1">Type to search all projects</p>
            )}
            {/* Results count */}
            {searchQuery && !isSearching && displayProjects.length > 0 && (
              <p className="text-xs text-slate-500 mt-1 px-1">{displayProjects.length} results found</p>
            )}
          </div>

          {/* Project List */}
          <div 
            ref={listRef}
            className="overflow-y-auto"
            style={{ maxHeight: '260px' }}
          >
            {isSearching ? (
              <div className="px-4 py-8 text-center">
                <Loader2 size={24} className="mx-auto text-slate-400 mb-2 animate-spin" />
                <p className="text-sm text-slate-500">Searching projects...</p>
              </div>
            ) : displayProjects.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Building2 size={24} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">
                  {searchQuery ? 'No projects match your search' : 'Type to search projects'}
                </p>
                {searchQuery && onSearch && (
                  <p className="text-xs text-slate-400 mt-1">Try a different search term</p>
                )}
              </div>
            ) : (
              displayProjects.map((project, index) => {
                const isSelected = selectedIds.includes(project.project_id);
                return (
                  <div
                    key={project.project_id}
                    data-testid={`project-option-${project.project_id}`}
                    onClick={() => toggleProject(project)}
                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors
                      ${isSelected ? 'bg-red-50' : 'hover:bg-slate-50'}
                      ${index !== displayProjects.length - 1 ? 'border-b border-slate-50' : ''}
                    `}
                  >
                    {/* Checkbox indicator */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
                      ${isSelected 
                        ? 'bg-[#5B3DF5] border-[#EF4444]' 
                        : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                    </div>
                    
                    {/* Project info */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${isSelected ? 'font-medium text-slate-900' : 'text-slate-700'}`}>
                        {project.name || project.project_name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {project.location}{project.city ? `, ${project.city}` : ''}
                      </p>
                    </div>
                    
                    {/* Price range hint */}
                    {project.price_min_lakh && (
                      <span className="text-xs text-slate-400 flex-shrink-0">
                        ₹{project.price_min_lakh}L+
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer with selection count */}
          {selectedIds.length > 0 && (
            <div className="px-3 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-xs text-slate-600 font-medium">
                {selectedIds.length} project{selectedIds.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => onChange([])}
                className="text-xs text-[#5B3DF5] hover:text-red-700 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {/* Validation Error */}
      {error && (
        <p className="mt-1 text-xs text-[#5B3DF5]">Please select at least one project</p>
      )}
    </div>
  );
};

export default ProjectMultiSelect;
